import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Category } from '../types/category';
import { categoriesService, CategoryCreateInput, CategoryUpdateInput } from '../services/supabase/categories.service';
import logger from '../utils/logger';

/**
 * Hook personnalisé pour gérer les catégories
 * Fournit des fonctions pour charger, créer, mettre à jour et supprimer des catégories
 * ainsi que pour gérer leur visibilité
 */
export const useCategories = () => {
  const { state, dispatch } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoadDone = useRef(false);

  /**
   * Charge toutes les catégories depuis Supabase
   */
  const loadCategories = useCallback(async () => {
    // Éviter les chargements multiples
    if (isInitialLoadDone.current) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const categories = await categoriesService.getAll();
      
      // Mettre à jour l'état global
      dispatch({ type: 'SET_CATEGORIES', payload: categories });
      
      // Initialiser les catégories visibles en fonction de leur propriété isVisible
      const visibleCategoriesSet = new Set<string>();
      categories.forEach((category: Category) => {
        // Si isVisible est undefined, on considère la catégorie comme visible par défaut
        if (category.isVisible !== false) {
          visibleCategoriesSet.add(category.id);
        }
      });
      
      dispatch({ type: 'SET_VISIBLE_CATEGORIES', payload: visibleCategoriesSet });
      
      isInitialLoadDone.current = true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      logger.error('Erreur lors du chargement des catégories:', error);
      setError(`Erreur lors du chargement des catégories: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  /**
   * Charge les catégories au montage du composant
   */
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  /**
   * Ajoute une nouvelle catégorie
   * @param categoryData Les données de la nouvelle catégorie
   * @returns La catégorie créée
   */
  const addCategory = useCallback(async (categoryData: CategoryCreateInput): Promise<Category> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Créer la catégorie dans Supabase avec isVisible = true par défaut
      const newCategory = await categoriesService.create({
        ...categoryData,
        isVisible: true
      });
      
      // Initialiser les préférences utilisateur pour cette catégorie
      try {
        const { userCategoryPreferencesService } = await import('../services/supabase/user-category-preferences.service');
        await userCategoryPreferencesService.initializePreferenceForCategory(newCategory.id);
      } catch (prefError) {
        logger.error('Erreur lors de l\'initialisation des préférences:', prefError);
        // Ne pas bloquer la création de la catégorie si les préférences échouent
      }
      
      // Mettre à jour l'état global
      // Utiliser SET_CATEGORIES au lieu de ADD_CATEGORY pour compatibilité avec le reducer existant
      const updatedCategories = [...state.categories, newCategory];
      dispatch({ type: 'SET_CATEGORIES', payload: updatedCategories });
      
      // Ajouter la nouvelle catégorie aux catégories visibles
      const newVisibleCategories = new Set(state.visibleCategories);
      newVisibleCategories.add(newCategory.id);
      dispatch({ type: 'SET_VISIBLE_CATEGORIES', payload: newVisibleCategories });
      
      return newCategory;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      logger.error('Erreur lors de la création de la catégorie:', error);
      setError(`Erreur lors de la création de la catégorie: ${errorMessage}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, state.categories]);

  /**
   * Met à jour une catégorie existante
   * @param categoryId L'ID de la catégorie à mettre à jour
   * @param categoryData Les données à mettre à jour
   * @returns La catégorie mise à jour
   */
  const updateCategory = useCallback(async (categoryId: string, categoryData: CategoryUpdateInput): Promise<Category> => {
    setIsLoading(true);
    setError(null);
    
    try {
      logger.debug("Début de la mise à jour de la catégorie", { categoryId, categoryData });
      
      // Mettre à jour la catégorie dans Supabase
      const updatedCategory = await categoriesService.update(categoryId, categoryData);
      
      // Mettre à jour l'état global
      // Utiliser SET_CATEGORIES au lieu de UPDATE_CATEGORY pour compatibilité avec le reducer existant
      const updatedCategories = state.categories.map(cat =>
        cat.id === categoryId ? updatedCategory : cat
      );
      dispatch({ type: 'SET_CATEGORIES', payload: updatedCategories });
      
      logger.debug("Catégorie mise à jour avec succès", updatedCategory);
      
      return updatedCategory;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      logger.error(`Erreur lors de la mise à jour de la catégorie ${categoryId}:`, error);
      setError(`Erreur lors de la mise à jour de la catégorie: ${errorMessage}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, state.categories]);

  /**
   * Supprime une catégorie et tous ses marqueurs associés
   * @param categoryId L'ID de la catégorie à supprimer
   * @returns true si la suppression a réussi, false sinon
   */
  const deleteCategory = useCallback(async (categoryId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Demander confirmation à l'utilisateur
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie et tous ses marqueurs associés ?')) {
        return false;
      }
      
      // Supprimer la catégorie dans Supabase et récupérer les IDs des marqueurs supprimés
      const deletedMarkerIds = await categoriesService.delete(categoryId);
      
      // Mettre à jour l'état global avec les IDs des marqueurs supprimés
      dispatch({ 
        type: 'DELETE_CATEGORY', 
        payload: { 
          categoryId, 
          deletedMarkerIds 
        } 
      });
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      logger.error(`Erreur lors de la suppression de la catégorie ${categoryId}:`, error);
      setError(`Erreur lors de la suppression de la catégorie: ${errorMessage}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  /**
   * Bascule la visibilité d'une catégorie
   * @param categoryId L'ID de la catégorie dont la visibilité doit être basculée
   */
  const toggleCategoryVisibility = useCallback((categoryId: string) => {
    dispatch({ type: 'TOGGLE_CATEGORY_VISIBILITY', payload: categoryId });
  }, [dispatch]);

  /**
   * Sélectionne une catégorie
   * @param category La catégorie à sélectionner ou null pour désélectionner
   */
  const selectCategory = useCallback((category: Category | null) => {
    dispatch({ type: 'SELECT_CATEGORY', payload: category });
  }, [dispatch]);

  return {
    categories: state.categories,
    visibleCategories: state.visibleCategories,
    selectedCategory: state.selectedCategory,
    isLoading,
    error,
    loadCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryVisibility,
    selectCategory
  };
};

